import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Handshake, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

function PartnerRow({ p, onSaved, onVip, onFounding }) {
  const [form, setForm] = useState({ amount_gbp: "", date: today(), note: "" });
  const [busy, setBusy] = useState(false);
  const record = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partners/${p.code}/payouts`, { ...form, amount_gbp: parseFloat(form.amount_gbp) });
      onSaved(data.partners); setForm({ amount_gbp: "", date: today(), note: "" }); toast.success("Payout recorded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't record payout"); }
    setBusy(false);
  };
  const remove = async (id) => {
    try { const { data } = await api.delete(`/admin/partners/payouts/${id}`); onSaved(data.partners); } catch { toast.error("Couldn't remove payout"); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-5" data-testid={`admin-partner-${p.code}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-serif text-xl">{p.name || p.email}</p>
          <p className="text-xs text-muted-foreground">{p.email} · {p.vanity ? `${p.vanity} (${p.code})` : `code ${p.code}`} · {Math.round(p.rate * 100)}%</p>
          <div className="mt-2 flex gap-1" data-testid={`admin-partner-tier-${p.code}`}>
            {[["partner", "Partner"], ["select", "Select"], ["vip", "VIP · 30%"]].map(([t, l]) => (
              <button key={t} onClick={() => onVip(p.code, t)} data-testid={`admin-partner-tier-${p.code}-${t}`}
                className={`rounded-full px-3 py-1 text-xs border ${p.tier === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{l}</button>
            ))}
            <button onClick={async (e) => { const b = e.currentTarget; b.disabled = true; await onFounding(p.code, !p.founding); b.disabled = false; }} data-testid={`admin-partner-founding-${p.code}`}
              className={`rounded-full px-3 py-1 text-xs border ${p.founding ? "bg-amber-300/20 text-amber-200 border-amber-300/40" : "border-border text-muted-foreground"}`}>{p.founding ? "Founding ✓" : "Founding"}</button>
          </div>
        </div>
        <div className="flex gap-5 text-right text-xs text-muted-foreground">
          <div><div className="text-foreground text-base">{p.signups}</div>signups</div>
          <div><div className="text-foreground text-base">{p.paid}</div>paying</div>
          <div><div className="text-foreground text-base">{gbp(p.earned_gbp)}</div>earned</div>
          <div><div className="text-foreground text-base">{gbp(p.paid_out_gbp)}</div>paid</div>
          <div><div className="text-amber-400 text-base" data-testid={`admin-partner-owed-${p.code}`}>{gbp(p.owed_gbp)}</div>owed</div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input type="number" step="0.01" min="0" placeholder={`Amount (owed ${gbp(p.owed_gbp)})`} value={form.amount_gbp} onChange={(e) => setForm({ ...form, amount_gbp: e.target.value })} className="sm:w-48" data-testid={`admin-payout-amount-${p.code}`} />
        <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="sm:w-44" data-testid={`admin-payout-date-${p.code}`} />
        <Input placeholder="Note (e.g. PayPal ref)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="flex-1" data-testid={`admin-payout-note-${p.code}`} />
        <Button onClick={record} disabled={busy || !form.amount_gbp} className="rounded-full" data-testid={`admin-payout-save-${p.code}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark as paid"}
        </Button>
      </div>
      {p.payouts.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {p.payouts.map((x) => (
            <div key={x.id} className="flex items-center justify-between text-xs text-muted-foreground" data-testid={`admin-payout-${x.id}`}>
              <span>{x.date} · {gbp(x.amount_gbp)}{x.note ? ` · ${x.note}` : ""}</span>
              <button onClick={() => remove(x.id)} className="hover:text-destructive" aria-label="Remove payout" data-testid={`admin-payout-remove-${x.id}`}><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPartners() {
  const [data, setData] = useState(null);
  const load = (d) => setData(d);
  useEffect(() => { api.get("/admin/partners").then((r) => load(r.data)).catch(() => load({ partners: [], requests: [] })); }, []);
  const saved = (partners) => api.get("/admin/partners").then((r) => load(r.data)).catch(() => setData((d) => ({ ...d, partners })));
  const founding = async (code, on) => {
    try { const { data: d } = await api.post(`/admin/partners/${code}/founding`, { on }); load(d); toast.success(on ? "Founding Partner badge given" : "Founding Partner badge removed"); }
    catch { toast.error("Couldn't update the badge"); }
  };
  const vip = async (code, tier) => {
    try { const { data: d } = await api.post(`/admin/partners/${code}/tier`, { tier }); load(d); toast.success(tier === "vip" ? "Now a VIP Partner (30%)" : tier === "select" ? "Now a Select Partner" : "Now a Partner"); }
    catch { toast.error("Couldn't update tier"); }
  };
  const list = data?.partners;
  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-partners">
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Partners</p>
      <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><Handshake className="w-6 h-6 text-primary" /> Partner payouts</h2>
      <p className="text-muted-foreground mb-6 max-w-xl">Partners earn 25% (VIP strategic partners 30%) of what each referred member pays for their membership in their first 12 months (top-ups excluded), Partner → Select → VIP: you promote partners by hand; VIP unlocks 30%. The month's #1 is featured as Partner of the Month (no cash prize). Record each payout you send.</p>
      {data?.requests?.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-400/[0.05] p-5 mb-6" data-testid="admin-payout-requests">
          <p className="text-sm font-medium mb-3">Payout requests</p>
          {data.requests.map((r) => (
            <div key={r.id} className="text-xs py-2 border-t border-border first:border-0" data-testid={`admin-request-${r.code}`}>
              <span className="text-foreground">{r.name}</span> ({r.email}) · <span className="text-amber-400">{gbp(r.amount_gbp)}</span>{r.owed_now_gbp != null && Math.abs(r.owed_now_gbp - r.amount_gbp) > 0.01 ? ` (owed now ${gbp(r.owed_now_gbp)})` : ""} · {r.method === "bank"
                ? `Bank: ${r.details.account_name}, ${r.details.sort_code}, ${r.details.account_number}` : `PayPal: ${r.details.paypal_email}`} · {r.created_at?.slice(0, 10)}
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground mt-2">Recording a payout for a partner below marks their request as paid.</p>
        </div>
      )}
      {list == null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> :
        list.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-partners-empty">No partners with signups yet.</p> :
        <div className="space-y-4">{list.map((p) => <PartnerRow key={p.code} p={p} onSaved={saved} onVip={vip} onFounding={founding} />)}</div>}
    </div>
  );
}
